import { Box, Skeleton, SimpleGrid, useToast, Flex } from '@chakra-ui/react';
import _ from 'lodash';
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import {
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';

import { FundingRequestStatusEnumGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { useGetFundingRequestsWithEventDetailsLazyQueryGQLType } from 'packages/funding-requests/requests/requests.gql.generated';
import { HistoryTable } from 'packages/funding-requests/ui/history/HistoryTable';
import { statusConfigs } from 'packages/funding-requests/ui/shared/StatusBadge';

const { transformToStartOfDay, transformToEndOfDay } = commonDate;

export const FundingRequestHistoryViewContainer = ({
  allowedStatuses,
  activeCreditFacilities,
}: {
  allowedStatuses: FundingRequestStatusEnumGQLType[];
  activeCreditFacilities?: { id: string; name: string }[];
}) => {
  const toast = useToast();

  const columnDisplaySettingsButtonRef = useRef<HTMLDivElement>(null!);
  const { creditFacilityId } = useCreditFacilityContext();

  const fundingRequestStatuses = useMemo(() => {
    const allStatuses: FundingRequestStatusEnumGQLType[] = _.intersection<FundingRequestStatusEnumGQLType>(
      commonTypeguards.getKeys(statusConfigs),
      allowedStatuses,
    );
    const options = allStatuses.map((status) => ({
      value: status,
      label: statusConfigs[status].label,
    }));
    const initStatuses = allStatuses.filter(
      (status) =>
        status !== 'canceled' &&
        status !== 'canceledLoanEdit' &&
        status !== 'overriddenFromLoanEdit',
    );
    return { initStatuses, options };
  }, [allowedStatuses]);

  const [selectedStatuses, setSelectedStatuses] = useState<
    FundingRequestStatusEnumGQLType[]
  >(fundingRequestStatuses.initStatuses);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [creditFacilityIds, setCreditFacilityIds] = useState<string[]>(
    activeCreditFacilities
      ? activeCreditFacilities.map((facility) => facility.id)
      : [creditFacilityId],
  );

  useEffect(() => {
    if (!activeCreditFacilities) {
      setCreditFacilityIds((oldArray) => {
        const newArray = [creditFacilityId];
        return _.isEqual(oldArray, newArray) ? oldArray : newArray;
      });
    }
  }, [creditFacilityId, activeCreditFacilities]);

  const { pageLimit, setPageLimit, options } = usePaginationLimit(5, [
    5,
    10,
    25,
  ]);

  const [
    getFundingRequests,
    { loading: fundingRequestHistoryLoading, data: fundingRequestHistoryData },
  ] = useGetFundingRequestsWithEventDetailsLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  const fetchFundingRequests = useCallback(
    (cursor?: { after: string } | { before: string }) => {
      return getFundingRequests({
        variables: {
          limit: pageLimit,
          creditFacilityIds,
          ...cursor,
          startDate: startDate
            ? transformToStartOfDay(startDate).getTime()
            : null,
          endDate: endDate ? transformToEndOfDay(endDate).getTime() : null,
          statuses: selectedStatuses,
        },
      });
    },
    [
      creditFacilityIds,
      endDate,
      getFundingRequests,
      pageLimit,
      selectedStatuses,
      startDate,
    ],
  );

  useEffect(() => {
    fetchFundingRequests();
  }, [
    startDate,
    endDate,
    pageLimit,
    creditFacilityIds,
    getFundingRequests,
    selectedStatuses,
    fetchFundingRequests,
  ]);

  if (!fundingRequestHistoryData || fundingRequestHistoryLoading) {
    return (
      <Skeleton height="600px" data-testid="funding-request-history-loading" />
    );
  }

  const {
    fundingRequests: {
      nodes: fundingRequests,
      pageInfo: { hasNextPage, hasPreviousPage, startCursor, endCursor },
    },
  } = fundingRequestHistoryData;

  return (
    <Box height="100%" width="100%">
      <PaginationControl
        filters={
          <SimpleGrid
            sx={{
              gridTemplateColumns: {
                '2xl': 'repeat(4, auto)',
                base: 'repeat(2, auto)',
              },
              width: '100%',
            }}
            spacingX={8}
            spacingY={8}
          >
            {activeCreditFacilities && (
              <Box>
                <IsolatedMultiSelect
                  applyButtonText="Apply filters"
                  buttonProps={{
                    minWidth: '100%',
                  }}
                  counterLabel="Credit facilities"
                  initialSelections={creditFacilityIds}
                  isSearchable
                  onApplyOptions={setCreditFacilityIds}
                  options={activeCreditFacilities.map((facility) => ({
                    label: facility.name,
                    value: facility.id,
                  }))}
                  optionsHeader="Credit facilities"
                  placeholderText="Select credit facilities"
                  showSelectedCount
                  titleText="All credit facilities"
                />
              </Box>
            )}
            <Box>
              <IsolatedMultiSelect<FundingRequestStatusEnumGQLType>
                applyButtonText="Apply filter"
                buttonProps={{
                  minWidth: '100%',
                }}
                counterLabel="Statuses"
                initialSelections={selectedStatuses}
                isSearchable
                onApplyOptions={(statuses) => setSelectedStatuses(statuses)}
                options={fundingRequestStatuses.options}
                optionsHeader="Statuses"
                placeholderText="Select statuses"
                showSelectedCount
                titleText="All statuses"
              />
            </Box>
            {/* FilterDatePicker has maxWidth of 500px, which doesn't quite work here, so it has to be manually readjusted */}
            <Box sx={{ '> *': { maxWidth: '515px' } }}>
              <FilterDatePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            </Box>
            <Box>
              {/* @timmydoza - Here we use the columnDisplaySettingsButtonRef to render the ColumnDisplaySettingsButton 
                  through a portal. Both ColumnDisplaySettingsButton and HistoryTable need access to the 
                  same information, so something had to be lifted up the component tree in order to get this
                  to work. I found that lifting a single ref up was simpler than lifting a ton of other things. */}
              <Flex
                justify="end"
                as="div"
                gap="0.5em"
                ref={columnDisplaySettingsButtonRef}
              />
            </Box>
          </SimpleGrid>
        }
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        options={options}
        limitLabel="Items Per Page"
        hasPreviousPage={hasPreviousPage}
        previousLabel="Newer"
        onPrevious={() =>
          fetchFundingRequests({ after: startCursor as string })
        }
        nextLabel="Older"
        hasNextPage={hasNextPage}
        onNext={() => fetchFundingRequests({ before: endCursor as string })}
      >
        <>
          <Box height="20px" />
          <HistoryTable
            fundingRequests={fundingRequests}
            columnDisplaySettingsButtonRef={columnDisplaySettingsButtonRef}
          />
        </>
      </PaginationControl>
    </Box>
  );
};
